import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {BreadcrumbModule} from 'primeng/breadcrumb';
import {CardModule} from 'primeng/card';
import {
  ProjectDetailResponse,
  ProjectUserControllerService,
  ProjectUserDetailResponse,
} from '@frontend/shared-api';
import {ProjectStepStateService} from '../../project-sequence/project-step-state.service';
import {ProjectDetailListComponent} from './list/project-detail-list.component';
import {TranslocoDirective, TranslocoService} from '@jsverse/transloco';
import {ProjectDetailCompanyListComponent} from './components/company-list/project-detail-company-list.component';
import {
  AvatarUser,
  UserAvatarGroupComponent,
} from './components/user-avatar-group/user-avatar-group.component';
import {BackgroundCardComponent} from '../../../../shared/components/titled-background-card/background-card.component';
import {APP_ROUTES} from '../../../../app.routes';
import {LoadingComponent} from '../../../../shared/components/loading/loading.component';

@Component({
  selector: 'app-project-details',
  imports: [
    BreadcrumbModule,
    CardModule,
    ProjectDetailListComponent,
    TranslocoDirective,
    ProjectDetailCompanyListComponent,
    UserAvatarGroupComponent,
    BackgroundCardComponent,
    LoadingComponent,
  ],
  templateUrl: './project-details.component.html',
})
export class ProjectDetailsComponent implements OnInit {
  project!: ProjectDetailResponse;
  projectUsers: AvatarUser[] = [];
  usersLoading = signal(true);

  loading = computed(() => this.usersLoading() || this.stepStateService.loading());

  private readonly route = inject(ActivatedRoute);
  private readonly transloco = inject(TranslocoService);
  private readonly projectUserService = inject(ProjectUserControllerService);
  private readonly stepStateService = inject(ProjectStepStateService);

  readonly breadcrumbItems = computed(() => {
    const projectName = this.project.name;
    return [
      {label: this.transloco.translate('project.details.list'), routerLink: APP_ROUTES.PROJECT},
      {label: projectName},
    ];
  });

  ngOnInit() {
    this.project = this.route.snapshot.data['project'] as ProjectDetailResponse;
    this.loadProjectUsers();
  }

  private loadProjectUsers() {
    if (this.project.projectManager) {
      this.projectUsers.push({
        fullName: this.project.projectManager,
      });
    }

    this.projectUserService.getProjectUsersWithDetails({projectId: this.project.id}).subscribe({
      next: (users: ProjectUserDetailResponse[]) => {
        const usersToAdd = users
          .filter((user) => this.getUserFullName(user) !== this.project.projectManager)
          .map((user) => ({
            fullName: this.getUserFullName(user),
          }));

        this.projectUsers = [...this.projectUsers, ...usersToAdd];
        this.usersLoading.set(false);
      },
      error: () => {
        this.usersLoading.set(false);
      },
    });
  }

  private getUserFullName(user: ProjectUserDetailResponse): string {
    return `${user.firstName} ${user.lastName}`;
  }
}
